import { LOCATION_MODE } from '@/types/location-mode';
import { enrollmentKeys } from '@/hooks/use-enrollments';
import { getMarketingHomeServices } from '@/content/marketing/home-mock-data';
import { ServiceFormData } from '@/lib/validators/service';
import { parseCurrency } from '@/utils/parse-currency';
import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { isMarketingMockEnabled } from '@/lib/marketing-mock/is-marketing-mock-enabled';
import { getMarketingMockQueryOptions } from '@/lib/marketing-mock/query-options';
import api from '@/services/api';
import { actAsHeaders } from '@/store/actingProviderStore';
import { RECURRING_BOOKING_MODE } from '@/types/recurring-booking-mode';
import { Service } from '@/types/service';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

// Query keys
export const serviceKeys = {
  all: ['services'] as const,
  lists: () => [...serviceKeys.all, 'list'] as const,
  list: (filters?: { q?: string; isActive?: boolean }) =>
    [...serviceKeys.lists(), filters] as const,
  details: () => [...serviceKeys.all, 'detail'] as const,
  detail: (id: string) => [...serviceKeys.details(), id] as const,
  provider: () => [...serviceKeys.all, 'provider'] as const,
  of: (providerId: string) => [...serviceKeys.lists(), 'of', providerId] as const,
};

type PaginatedResponse<T> = {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
};

// Provider services
export function useProviderServices(filters?: {
  q?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: serviceKeys.list(filters),
    queryFn: async () => {
      const params: Record<string, any> = {};
      if (filters?.q) params.q = filters.q;
      if (filters?.isActive !== undefined) params.isActive = filters.isActive;
      // Sempre envia page e limit para garantir formato paginado
      params.page = filters?.page || 1;
      params.limit = filters?.limit || 10;

      const response = await api.get<PaginatedResponse<Service>>('/provider/services', {
        params,
      });

      // Garantir que sempre retorna formato paginado
      const data = response.data;
      if (Array.isArray(data)) {
        // Se vier como array (formato antigo), converter para formato paginado
        return {
          data,
          meta: {
            page: 1,
            limit: data.length,
            total: data.length,
            totalPages: 1,
            hasNextPage: false,
            hasPreviousPage: false,
          },
        };
      }

      return data;
    },
    enabled: isHydrated,
    ...getMarketingMockQueryOptions(
      isMarketingMockEnabled() ? getMarketingHomeServices(filters) : undefined,
    ),
  });
}

async function fetchServicesOf(providerId: string): Promise<Service[]> {
  const response = await api.get<PaginatedResponse<Service> | Service[]>('/provider/services', {
    params: { page: 1, limit: 100 },
    ...actAsHeaders(providerId),
  });
  const data = response.data;
  return Array.isArray(data) ? data : data.data;
}

/** Serviços de um profissional da equipe, sem trocar o foco ("Com quem?"). */
export function useServicesOf(providerId: string | null | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: serviceKeys.of(providerId ?? ''),
    queryFn: () => fetchServicesOf(providerId as string),
    enabled: isHydrated && !!providerId,
  });
}

/**
 * Serviços de vários profissionais de uma vez (repasse de agendamentos), no
 * mesmo cache do `useServicesOf`. Devolve por providerId; quem ainda carrega
 * fica de fora.
 */
export function useServicesOfMany(providerIds: string[]): Record<string, Service[]> {
  const isHydrated = useAuthHydration();

  return useQueries({
    queries: providerIds.map((providerId) => ({
      queryKey: serviceKeys.of(providerId),
      queryFn: () => fetchServicesOf(providerId),
      enabled: isHydrated,
    })),
    combine: (results) =>
      Object.fromEntries(
        results.flatMap((result, index) =>
          result.data ? [[providerIds[index], result.data] as const] : [],
        ),
      ),
  });
}

// Single service
export function useService(id: string | undefined) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: serviceKeys.detail(id || ''),
    queryFn: async () => {
      if (!id) throw new Error('Service ID is required');
      const response = await api.get<Service>(`/service/${id}`);
      return response.data;
    },
    enabled: isHydrated && !!id,
  });
}

/**
 * Formulário → corpo da API. Um único mapeamento para criar e editar, então os
 * dois caminhos mandam exatamente os mesmos campos.
 */
export function toServicePayload(data: Partial<ServiceFormData>) {
  const payload: Record<string, unknown> = {};

  const set = (key: string, value: unknown) => {
    if (value !== undefined) payload[key] = value;
  };

  set('name', data.name);
  set('description', data.description);
  set('isActive', data.isActive);
  set('allowedPaymentMethods', data.allowedPaymentMethods);
  set('priceDisplayMode', data.priceDisplayMode);
  set('durationMinutes', data.durationMinutes);
  set('slotCapacity', data.slotCapacity);
  set('isSubscription', data.isSubscription);
  // Assinatura é sempre recorrente — o backend normaliza, mas o payload já sai
  // coerente para a tela não mandar uma combinação impossível.
  set(
    'recurringBookingMode',
    data.isSubscription ? RECURRING_BOOKING_MODE.AUTO : data.recurringBookingMode,
  );

  // A cota só existe dentro da assinatura: fora dela o payload não manda nada
  // e o backend zera os dois campos.
  if (data.isSubscription) {
    set('subscriptionQuota', data.subscriptionQuota);
    set('subscriptionQuotaPeriod', data.subscriptionQuotaPeriod);
  }

  // Endereço e preço são "liga/desliga" no formulário: desligado volta a nulo.
  if (data.locationMode !== undefined) {
    payload.locationMode = data.locationMode;
    payload.address = data.locationMode === LOCATION_MODE.PROVIDER_SPACE ? data.address : null;
  }
  if (data.hasFixedPrice !== undefined) {
    payload.defaultPrice = data.hasFixedPrice
      ? (parseCurrency(data.defaultPrice ?? '') ?? null)
      : null;
  }

  // Cadência e validade só fazem sentido com recorrência ligada.
  if (
    data.isSubscription ||
    (data.recurringBookingMode && data.recurringBookingMode !== RECURRING_BOOKING_MODE.OFF)
  ) {
    set('recurringBillingInterval', data.recurringBillingInterval);
    set('recurringHorizonDays', data.recurringHorizonDays);
  }

  return payload;
}

// Create service mutation
export function useCreateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ServiceFormData) => {
      const response = await api.post<Service>('/service', toServicePayload(data));
      return response.data;
    },
    onSuccess: () => {
      // Invalidate services list
      queryClient.invalidateQueries({ queryKey: serviceKeys.provider() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}

// Update service mutation
export function useUpdateService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ServiceFormData> }) => {
      const response = await api.patch<Service>(`/service/${id}`, toServicePayload(data));
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: serviceKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: serviceKeys.provider() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}

// Configuração de agendamento do serviço (A2/A3): duração e capacidade.
// `durationMinutes: null` torna o serviço não agendável online.
export type ServiceSchedulingPatch = {
  id: string;
  durationMinutes?: number | null;
  slotCapacity?: number;
};

/** Aplica o patch no serviço `id` dentro de todas as listas em cache. */
function patchServiceInLists(
  queryClient: ReturnType<typeof useQueryClient>,
  { id, durationMinutes, slotCapacity }: ServiceSchedulingPatch,
) {
  queryClient.setQueriesData<PaginatedResponse<Service>>(
    { queryKey: serviceKeys.lists() },
    (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.map((service) =>
          service.id === id
            ? {
                ...service,
                ...(durationMinutes !== undefined ? { durationMinutes } : {}),
                ...(slotCapacity !== undefined ? { slotCapacity } : {}),
              }
            : service,
        ),
      };
    },
  );
}

/**
 * Salva o rascunho de agendamento dos serviços de uma vez só: um PATCH por
 * serviço que o prestador realmente mexeu, disparado no Salvar/Ativar — não
 * a cada toque numa pill de duração.
 */
export function useSaveServiceScheduling() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patches: ServiceSchedulingPatch[]) => {
      return Promise.all(
        patches.map(async ({ id, durationMinutes, slotCapacity }) => {
          const payload: Record<string, unknown> = {};
          if (durationMinutes !== undefined) payload.durationMinutes = durationMinutes;
          if (slotCapacity !== undefined) payload.slotCapacity = slotCapacity;

          const response = await api.patch<Service>(`/service/${id}`, payload);
          return response.data;
        }),
      );
    },
    onSuccess: (services) => {
      services.forEach((service) => {
        queryClient.setQueryData(serviceKeys.detail(service.id), service);
        patchServiceInLists(queryClient, {
          id: service.id,
          durationMinutes: service.durationMinutes ?? null,
          slotCapacity: service.slotCapacity,
        });
      });
      // Sem refetch: o cache já tem a resposta do servidor. Só marca stale
      // para as telas que montarem depois.
      queryClient.invalidateQueries({ queryKey: serviceKeys.all, refetchType: 'none' });
    },
  });
}

/** Atualiza a config de recorrência self-service de um serviço (prestador). */
export function useUpdateServiceRecurring() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      config,
    }: {
      id: string;
      config: Partial<
        Pick<
          Service,
          | 'isSubscription'
          | 'subscriptionQuota'
          | 'subscriptionQuotaPeriod'
          | 'recurringBookingMode'
          | 'recurringBillingInterval'
          | 'recurringHorizonDays'
        >
      >;
    }) => {
      const response = await api.patch<Service>(`/service/${id}`, config);
      return response.data;
    },
    onSuccess: (service) => {
      queryClient.setQueryData(serviceKeys.detail(service.id), service);
      queryClient.invalidateQueries({ queryKey: serviceKeys.provider() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
    },
  });
}

// Delete service mutation
export function useDeleteService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/service/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceKeys.provider() });
      queryClient.invalidateQueries({ queryKey: serviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: enrollmentKeys.provider() });
    },
  });
}
